// Libs
import { Avatar, Box, Button, Stack, Typography } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { DropzoneProps, DropzoneRef, useDropzone } from 'react-dropzone';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
import { BaseLabel } from '~/components/common';
// Others
import { IBaseFile } from '~/utils/interface/common';
import {
  FILE_UPLOAD_PROP_MIN_FILE,
  FILE_UPLOAD_PROP_MAX_FILE,
  DEFAULT_HEIGHT_UPLOAD_IMAGE,
  MAX_FILE_SIZE,
} from '~/utils/constants/common';
// Styles, images, icons
import styles from './BaseUploadImage.module.scss';
import { icons } from '~/assets';

export type BaseUploadImageProps = DropzoneProps &
  React.RefAttributes<DropzoneRef> & {
    label?: string;
    required?: boolean;
    uploadedFiles?: IBaseFile[];
    isMultiple?: boolean;
    errorMessage?: string;
    width?: number | string;
    height?: number | string;
    isButton?: boolean;
    onChange?: (files: IBaseFile[]) => void;
    onRemove?: (file: IBaseFile) => void;
  };

const cx = classNames.bind(styles);

const BaseUploadImage = (props: BaseUploadImageProps) => {
  //#region Destructuring Props
  const {
    label,
    required = false,
    isButton = true,
    isMultiple = true,
    maxFiles = isMultiple ? FILE_UPLOAD_PROP_MAX_FILE : FILE_UPLOAD_PROP_MIN_FILE,
    errorMessage,
    width,
    height = DEFAULT_HEIGHT_UPLOAD_IMAGE,
    onRemove,
    ...restProps
  } = props;
  delete restProps.onChange;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  const { getRootProps, getInputProps } = useDropzone({
    maxFiles,
    maxSize: MAX_FILE_SIZE,
    multiple: isMultiple,
    accept: {
      'image/png': [],
      'image/jpeg': [],
      'image/jpg': [],
      'image/gif': [],
    },
    ...restProps,
    onDrop: (acceptedFiles: Array<File>) => {
      const newFiles = acceptedFiles.map((item) => ({
        file: item,
        name: item.name,
        url: URL.createObjectURL(item),
      }));

      const updatedFiles = isMultiple ? [...uploadFiles, ...newFiles] : newFiles;

      setUploadFiles(updatedFiles);

      if (props.onChange) {
        props.onChange(updatedFiles);
      }
    },
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [uploadFiles, setUploadFiles] = useState<IBaseFile[]>([]);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (props?.uploadedFiles?.length) {
      setUploadFiles([...props.uploadedFiles.filter((uploadedFile) => !!uploadedFile?.url)]);
    }
  }, [props?.uploadedFiles]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleRemoveFile = (file: IBaseFile, fileIndex: number) => {
    const updatedFiles = uploadFiles.filter((_, index) => index !== fileIndex);
    setUploadFiles(updatedFiles);
    onRemove?.(file);

    if (props.onChange) {
      props.onChange(updatedFiles);
    }
  };

  const renderPreviewFileUpload = (file: IBaseFile, index: number) => {
    return (
      <Box component={'div'} className={cx('previewFile')} width={height} height={height}>
        <Avatar
          src={file?.url}
          alt={t('common_alt_img', { ns: 'common/base' })}
          variant='rounded'
          className={cx('image')}
        />

        <Button
          variant='contained'
          color='error'
          className={cx('deleteFile')}
          onClick={() => handleRemoveFile(file, index)}
        >
          <img
            src={icons.commonIconClose}
            alt={t('common_alt_img', { ns: 'common/base' })}
            className={cx('deleteFileIcon')}
          />
        </Button>
      </Box>
    );
  };
  //#endregion Handle Function

  return (
    <Stack>
      {label && <BaseLabel label={label} required={required} />}

      {uploadFiles.length < maxFiles && (
        <>
          <Box className={cx('container')} {...getRootProps()} sx={{ width, height }}>
            <input {...getInputProps()} className={cx('inputFile')} />

            <img src={icons.commonIconUploadFileImage} alt={t('common_alt_img')} className={cx('icon')} />

            <Typography className={cx('description')}>{t('common_upload_image_title')}</Typography>

            <Typography className={cx('description')}>{t('common_upload_image_description')}</Typography>

            {isButton && (
              <Button type='button' variant='contained' className={cx('buttonChoose')}>
                {t(maxFiles === 1 ? 'common_button_choose_file' : 'common_button_choose_files', { ns: 'common/base' })}
              </Button>
            )}
          </Box>

          <Typography className={cx('errorMessage')}>{errorMessage}</Typography>
        </>
      )}

      {uploadFiles?.length > 0 && (
        <Stack
          direction={'row'}
          alignItems={'center'}
          gap={6}
          className={cx('previewFileContainer', { multiple: isMultiple })}
        >
          {uploadFiles?.map((file, index) => (
            <Box key={index}>{renderPreviewFileUpload(file, index)}</Box>
          ))}
        </Stack>
      )}
    </Stack>
  );
};
export default memo(BaseUploadImage);
